import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Landmark, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ALL_COURSES, GOSPEL_COURSES } from "@/data/curriculum";
import { useAuth } from "@/context/AuthContext";

const OTHER = [...GOSPEL_COURSES.map((c) => ({ key: c.key, label: `${c.title} (£300)` })),
  { key: "", label: "1:1 sessions with Jason" }, { key: "", label: "Something else" }];
const field = "rounded-full border border-border bg-background px-4 py-2 text-sm focus:border-primary focus:outline-none";

const Detail = ({ label, value, testId }) => (
  <div className="flex items-center justify-between gap-3 border-t border-border py-2 text-sm" data-testid={testId}>
    <span className="text-muted-foreground">{label}</span>
    <button onClick={() => { navigator.clipboard?.writeText(value); toast.success(`${label} copied`); }} className="inline-flex items-center gap-1.5 font-mono font-semibold hover:text-primary">
      {value} <Copy size={12} />
    </button>
  </div>
);

// Payment page: pay by bank transfer, then tell Jason so he can confirm it and unlock the course.
export const BankTransfer = () => {
  const { user } = useAuth();
  const [bank, setBank] = useState(null);
  const [choice, setChoice] = useState("0");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  useEffect(() => { if (user) api.get("/payments/bank-details").then(({ data }) => setBank(data)).catch(() => {}); }, [user]);
  const items = [...ALL_COURSES.map((c) => ({ key: c.key, label: c.title })), ...OTHER];
  if (!user) return <p className="mt-4 text-sm text-muted-foreground"><Link to="/login" className="text-primary hover:underline">Sign in</Link> to see the bank details and your payment reference.</p>;
  if (!bank) return null;
  const send = async (e) => {
    e.preventDefault();
    const it = items[Number(choice)];
    setBusy(true);
    try { await api.post("/payments/bank-transfer", { item: it.label, course_key: it.key || null, amount: Number(amount), note }); setSent(true); toast.success("Thanks! Jason will confirm your payment soon."); }
    catch (err) { const d = err.response?.data?.detail; toast.error(typeof d === "string" ? d : "Please check the amount"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-5 grid gap-6 lg:grid-cols-2" data-testid="bank-transfer">
      <div className="rounded-2xl border border-border bg-card p-5">
        <p className="flex items-center gap-2 font-semibold"><Landmark size={16} className="text-primary" /> Bank details</p>
        <div className="mt-3">
          <Detail label="Account name" value={bank.account_name} testId="bank-account-name" />
          <Detail label="Account number" value={bank.account_number} testId="bank-account-number" />
          <Detail label="Sort code" value={bank.sort_code} testId="bank-sort-code" />
          <Detail label="Your reference" value={bank.reference} testId="bank-reference" />
        </div>
        <p className="mt-3 text-xs text-muted-foreground">Please use your reference so Jason can match your payment. Courses unlock once the money arrives and Jason confirms it (usually within 1 working day).</p>
      </div>
      {sent ? (
        <div className="rounded-2xl border border-primary/40 bg-primary/5 p-5 text-sm" data-testid="bank-transfer-sent">Thank you! Jason has been told about your payment and will email you once it's confirmed.</div>
      ) : (
        <form onSubmit={send} className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-5">
          <p className="font-semibold">I've sent a bank transfer</p>
          <select value={choice} onChange={(e) => setChoice(e.target.value)} className={field} data-testid="bank-transfer-item">
            {items.map((it, i) => <option key={i} value={i}>{it.label}</option>)}
          </select>
          <input type="number" min="1" step="0.01" required value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount sent (£)" className={field} data-testid="bank-transfer-amount" />
          <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="Anything Jason should know? (optional)" className={field} data-testid="bank-transfer-note" />
          <button type="submit" disabled={busy || !amount} data-testid="bank-transfer-submit" className="inline-flex items-center justify-center gap-1.5 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50">
            {busy && <Loader2 size={14} className="animate-spin" />} Tell Jason I've paid
          </button>
        </form>
      )}
    </div>
  );
};
